// Centralized UX copy taken directly from 02_UX_COPY_AND_SCREENS.md.
// Kept in one place so tone stays consistent and guardrails are auditable.
// Tone: casual, competitive, warm, slightly mischievous. Never clinical.
// The players provide the disrespect — the app never roasts a named member.

export const COPY = {
  invite: {
    headline: "You've been invited to a league.",
    description: "A game about how well y'all actually keep up with each other.",
    cta: "JOIN THE LEAGUE",
  },
  notifOnboarding: {
    headline: "Don't miss a drop.",
    body: "Questions drop at a random time on game days, and you only get a few hours to answer. Set this up once and your phone will tell you the moment it happens.",
    ctaAddHome: "ADD TO HOME SCREEN",
    ctaTurnOn: "TURN ON NOTIFICATIONS",
    emailFallback: "No problem. We'll use email as a backup.",
  },
  home: {
    dropLiveCta: "LET'S GET IT",
    lockedIn: "You're locked in.",
    lockedInSub: "Results drop when everyone finishes or time runs out.",
    nothingLive: "Nothing live right now.",
    stayReady: "Stay ready.",
    viewLeaderboard: "VIEW LEADERBOARD",
    checkInDue: "Give the league the update.",
    checkInCta: "CHECK IN",
    // "You're good for now (7 ready)" left the number dangling with nothing to
    // say what it counted.
    questionsReady: (n: number) =>
      `You've got ${n} question${n === 1 ? "" : "s"} about you ready to play.`,
    questionsReadySub: "They'll show up in drops when it's your turn. Add more any time.",
    recentlyHeading: "Recently",
  },
  checkIn: {
    headline: "Give the league the update.",
    body: "Talk or type for as long as you want. You don't have to hit everything. These are just places to go.",
    prompts: [
      "What's changed lately?",
      "Anything new with work or career?",
      "What's happening with family or relationships?",
      "Anything you're working toward?",
      "Anything big coming up?",
      "Anything taking up a lot of your time or headspace?",
      "Anything fun, random, or surprising the league probably doesn't know?",
    ],
    footer: "Share whatever feels worth sharing.",
    startTalking: "START TALKING",
    typeInstead: "TYPE INSTEAD",
    workingOnIt: "Working on it…",
    reviewTranscript: "Review transcript",
    addSomethingElse: "ADD SOMETHING ELSE",
    genericFollowUp:
      "Bet. One more thing. Anything new with work, family, something you're looking forward to, or something you've been trying to get done?",
    genFailed: "Couldn't build questions from that yet. Try again.",
  },
  candidates: {
    intro: "Here's what we could put in play.",
    labelQuestion: "QUESTION",
    labelOptions: "OPTIONS",
    labelCorrect: "CORRECT ANSWER",
    labelReveal: "WHEN EVERYBODY FINDS OUT",
    playIt: "PLAY IT",
    reframe: "REFRAME",
    edit: "EDIT",
    dontUse: "DON'T USE",
    locked: "Locked.",
    readyCount: (n: number) => `You've got ${n} question${n === 1 ? "" : "s"} ready to play.`,
  },
  drop: {
    // dynamic drop-open notification title
    dropTitle: (n: number) => {
      const word = n === 1 ? "One" : n === 2 ? "Two" : n === 3 ? "Three" : String(n);
      return `👀 ${word} question${n === 1 ? "" : "s"} just dropped.`;
    },
    // Both of these follow commissioner settings — never hardcode the numbers,
    // or the app contradicts the season config the commissioner actually chose.
    pushBody: (hours: number) => `You've got ${hours} hour${hours === 1 ? "" : "s"}.`,
    landingReadiness: (seconds: number) => `Hit START and the clock runs. ${seconds} seconds per question, one at a time.`,
    start: "START",
    letsGetIt: "LET'S GET IT",
    countLabel: (n: number) => `${n} question${n === 1 ? "" : "s"}`,
    aboutAMinute: "About a minute",
  },
  question: {
    progress: (i: number, total: number) => `${i} of ${total}`,
    locked: "Locked.",
    time: "Time.",
    subjectHeadline: "This one's about you. 😏",
    subjectSub: "Sit this one out. We'll see who knows their person.",
    subjectCta: "NEXT",
  },
  waiting: {
    lockedIn: "You're locked in.",
    resultsWhen: "Results drop when everyone finishes or time runs out.",
    doneCount: (done: number, total: number) => `${done} of ${total} players finished.`,
  },
  reveal: {
    pushTitle: "👀 Results are in.",
    pushBody: "See what everybody knew.",
    labelQuestion: "QUESTION",
    labelAnswer: "ANSWER",
    labelResult: "RESULT",
    labelContext: "CONTEXT",
    aboutYou: "ABOUT YOU",
    resultLine: (got: number, eligible: number) => `${got} of ${eligible} got this one.`,
    leaderboardUpdated: "LEADERBOARD UPDATED",
    seeStandings: "SEE STANDINGS",
  },
  leaderboard: {
    scoring: "1 point per correct answer. No speed bonus.",
  },
} as const;

// Aggregate-only reveal reactions. These react to the *count*, never to a named
// person. Never say "nobody knows X" or interpret a relationship.
export function revealReaction(got: number, eligible: number): string {
  if (eligible <= 0) return "";
  if (got === eligible) return "Everybody knew this one. 👀";
  if (got === 0) return `Nobody had this one. 😂`;
  if (got * 2 === eligible) return "Half the league had it.";
  if (got === 1) return `Sheesh. 1 of ${eligible} got this one. 😂`;
  return `${got} of ${eligible} had it.`;
}
